/**
 * L2 Subtabs (H-2026-09-12-135a) — the mode-scoped horizontal tab bar.
 *
 * Standard UI layering (bao 2026-09-12): L1 = rail mode, L2 = the horizontal
 * tabs under the page header (this module), drawer = on-demand detail panel.
 * An L2 tab switches the WORKSPACE VIEW inside a mode; it is routed state, so
 * it lives in the URL (`?mode=gallery&tab=favorites`) and survives refresh /
 * share. The drawer never enters the URL.
 *
 * Responsibilities:
 *   - registerSubtabs():  a mode declares its 1-4 tabs (id + label + hooks)
 *   - setSubtab():        validated switch → store (`subtabs.<mode>`) + URL
 *                         replaceState + SUBTAB_CHANGE event + tab lifecycle
 *   - initSubtabs():      boot — apply the deep-linked ?tab=, bind bar clicks,
 *                         re-render the bar on every MODE_ACTIVATED
 *
 * Rendering stays in page-frame.js (applySubtabBar); this module owns state.
 * A mode with <2 registered tabs gets NO bar (task card: single-tab modes
 * hide the strip).
 */

import { store } from './store.js'
import { eventBus, EVENTS } from './event-bus.js'
import { applySubtabBar } from '../modules/page-frame/page-frame.js'

// Task card rule: 2-4 tabs per mode (1 allowed but hides the bar); more than
// 4 means the mode should split into separate L1 entries.
const MAX_TABS = 4

// mode -> frozen tab defs: [{ id, label, default?, onActivate?, onDeactivate? }]
const registry = new Map()

/**
 * Register the L2 tabs for a mode. Called at module scope by the mode module
 * (mirrors registerMode). Throws on a malformed declaration — a bad tab
 * contract is a boot-time bug, not a runtime state.
 * @param {string} mode
 * @param {Array<{ id: string, label: string, default?: boolean,
 *   onActivate?: Function, onDeactivate?: Function }>} tabs
 */
export function registerSubtabs(mode, tabs) {
  if (!mode || typeof mode !== 'string') {
    throw new Error('[Subtabs] registerSubtabs requires a mode name')
  }
  if (!Array.isArray(tabs) || tabs.length < 1 || tabs.length > MAX_TABS) {
    throw new Error(`[Subtabs] "${mode}" must declare 1-${MAX_TABS} tabs`)
  }
  const ids = new Set()
  for (const tab of tabs) {
    if (!tab || typeof tab.id !== 'string' || !tab.id) {
      throw new Error(`[Subtabs] "${mode}" tab missing a string id`)
    }
    if (typeof tab.label !== 'string' || !tab.label) {
      throw new Error(`[Subtabs] "${mode}" tab "${tab.id}" missing a label`)
    }
    if (ids.has(tab.id)) {
      throw new Error(`[Subtabs] "${mode}" duplicate tab id "${tab.id}"`)
    }
    ids.add(tab.id)
  }
  registry.set(mode, Object.freeze(tabs.map((t) => ({ ...t }))))
}

/** Pure: the registered tab defs for a mode ([] when none). */
export function resolveSubtabs(mode) {
  return registry.get(mode) || []
}

/** Pure: the mode's default tab id — explicit `default: true`, else first. */
export function resolveDefaultSubtab(mode) {
  const tabs = resolveSubtabs(mode)
  if (!tabs.length) return null
  return (tabs.find((t) => t.default) || tabs[0]).id
}

/**
 * Pure: validate a requested tab against the registry. Unknown/missing ids
 * fall back to the mode default so a stale ?tab= never blanks a view.
 */
export function resolveActiveSubtab(mode, requested) {
  const tabs = resolveSubtabs(mode)
  if (requested && tabs.some((t) => t.id === requested)) return requested
  return resolveDefaultSubtab(mode)
}

/** The active tab id for a mode (store value validated against the registry). */
export function getActiveSubtab(mode) {
  return resolveActiveSubtab(mode, store.getState(`subtabs.${mode}`))
}

/**
 * Pure: parse the deep-link target out of a query string. Returns
 * { mode, tab } with nulls for absent params — no validation here (that is
 * resolveActiveSubtab's job, applied by the caller).
 * @param {string} search e.g. window.location.search
 */
export function resolveSubtabFromURL(search) {
  const params = new URLSearchParams(search || '')
  return { mode: params.get('mode'), tab: params.get('tab') }
}

/**
 * Write `?mode=X&tab=Y` into the URL (replaceState — tab switches are view
 * state, not history entries). Modes with <2 tabs drop the tab param: their
 * single view needs no qualifier.
 */
export function syncSubtabURL(mode, tabId) {
  try {
    const url = new URL(window.location.href)
    url.searchParams.set('mode', mode)
    if (tabId && resolveSubtabs(mode).length >= 2) {
      url.searchParams.set('tab', tabId)
    } else {
      url.searchParams.delete('tab')
    }
    window.history.replaceState(null, '', url)
  } catch {
    /* no window/history (non-browser host) — URL sync is best-effort */
  }
}

/**
 * Switch the active L2 tab for a mode.
 * Lifecycle hooks (onDeactivate of the outgoing tab, onActivate of the
 * incoming) fire ONLY when the mode is the live one — background tab writes
 * (e.g. the boot deep-link applied before the first activate) must not run
 * view side effects. SUBTAB_CHANGE always fires on a real change.
 * @returns {boolean} whether the switch was applied
 */
export function setSubtab(mode, tabId, { syncUrl = true } = {}) {
  const tabs = resolveSubtabs(mode)
  if (!tabs.length) return false
  const next = tabs.find((t) => t.id === tabId)
  if (!next) {
    console.warn(`[Subtabs] Unknown tab "${tabId}" for mode "${mode}"`)
    return false
  }

  const prevId = getActiveSubtab(mode)
  if (prevId === tabId) return true

  const prev = tabs.find((t) => t.id === prevId)
  store.setState(`subtabs.${mode}`, tabId)

  const isLive = store.getState('mode') === mode
  if (isLive) {
    if (syncUrl) syncSubtabURL(mode, tabId)
    applySubtabBar(mode, { tabs, activeId: tabId })
    if (prev?.onDeactivate) {
      try { prev.onDeactivate(mode) }
      catch (e) { console.error(`[Subtabs] onDeactivate "${prevId}":`, e) }
    }
    if (next.onActivate) {
      try { next.onActivate(mode) }
      catch (e) { console.error(`[Subtabs] onActivate "${tabId}":`, e) }
    }
  }

  eventBus.emit(EVENTS.SUBTAB_CHANGE, mode, tabId, prevId)
  return true
}

/**
 * Apply the boot deep-link (?mode=X&tab=Y) into the store. Invalid/missing
 * tabs fall back to the mode default; a tab for a mode that declares no
 * subtabs is ignored. No URL rewrite, no hooks — the router's first activate
 * owns view side effects.
 */
export function applyInitialSubtabFromURL() {
  const { mode: modeParam, tab } = resolveSubtabFromURL(window.location.search)
  if (!tab) return
  const mode = modeParam || store.getState('mode')
  if (!mode || !resolveSubtabs(mode).length) return
  store.setState(`subtabs.${mode}`, resolveActiveSubtab(mode, tab))
}

let initialized = false

/**
 * Boot the L2 layer. Call BEFORE initRouter(): the deep-linked tab must be in
 * the store before the first mode activate reads it, and the MODE_ACTIVATED
 * listener must be in place to render the bar on both the initial activate
 * (initRouter fires it manually) and every later setMode.
 */
export function initSubtabs() {
  if (initialized) return
  initialized = true

  applyInitialSubtabFromURL()

  // Bar click delegation (one listener for all modes; the bar buttons are
  // re-rendered per mode so per-button binding would go stale).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest?.('[data-subtab]')
    if (!btn || !btn.closest('#stageSubtabs')) return
    setSubtab(store.getState('mode'), btn.dataset.subtab)
  })

  // Render the bar + normalize the URL on every mode entry. MODE_ACTIVATED
  // (not MODE_CHANGE) covers both setMode and initRouter's manual first fire.
  eventBus.on(EVENTS.MODE_ACTIVATED, (mode) => {
    const tabs = resolveSubtabs(mode)
    const activeId = getActiveSubtab(mode)
    applySubtabBar(mode, { tabs, activeId })
    if (tabs.length) syncSubtabURL(mode, activeId)
  })
}

/** Test hook: wipe registry + boot flag + subtab state between suites. */
export function _resetSubtabsForTests() {
  registry.clear()
  initialized = false
  store.setState('subtabs', {})
}

export default {
  registerSubtabs,
  resolveSubtabs,
  resolveDefaultSubtab,
  resolveActiveSubtab,
  getActiveSubtab,
  setSubtab,
  syncSubtabURL,
  initSubtabs,
}
